import { useState } from 'react';
import { Check, ChevronDown, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import type { Sporsmal } from '../../api/reise';
import { sporOss, type Radgiversvar } from '../../api/sporOss';

type Endring = NonNullable<Radgiversvar['foreslattEndring']>;

interface Innlegg {
  sporsmal: string;
  /** Svaret så langt, mens det strømmes. */
  tekst: string;
  svar: Radgiversvar | null;
  bekreftet: boolean;
}

interface Props {
  /** Spørsmålet kunden står på, så rådgiveren vet hvilken kunnskap som gjelder. */
  sporsmal: Sporsmal | null;
  /** Kunden bekrefter et foreslått endret svar. Gir om det ble lagret. */
  onBekreftEndring: (endring: Endring) => Promise<boolean>;
  /** Feil som ikke hører hjemme i panelet, som at økten har gått ut. Gir om feilen ble tatt hånd om. */
  onFeil: (e: unknown) => boolean;
}

/** Endringen som tekst: valgene, eller verdien slik den er. */
function endringstekst(endring: Endring): string {
  if (endring.valgte.length > 0) return endring.valgte.map((v) => v.tekst).join(', ');
  return typeof endring.verdi === 'string' || typeof endring.verdi === 'number' ? String(endring.verdi) : JSON.stringify(endring.verdi);
}

/**
 * «Spør oss» (T064): kunden spør underveis, og svaret strømmes inn. Svaret viser hva det bygger på. Et svar
 * om noe vi ikke vet sikkert, sier at vi sjekker det. Et forslag om å endre et svar lagres bare når kunden
 * bekrefter det.
 */
export default function SporOssPanel({ sporsmal, onBekreftEndring, onFeil }: Props) {
  const { t } = useTranslation();
  const [apen, setApen] = useState(false);
  const [tekst, setTekst] = useState('');
  const [samtale, setSamtale] = useState<Innlegg[]>([]);
  const [svarer, setSvarer] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  const oppdaterSiste = (endre: (innlegg: Innlegg) => Innlegg) =>
    setSamtale((s) => s.map((innlegg, i) => (i === s.length - 1 ? endre(innlegg) : innlegg)));

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const sporsmalet = tekst.trim();
    if (!sporsmalet || svarer) return;
    setFeil(null);
    setSvarer(true);
    setTekst('');
    setSamtale((s) => [...s, { sporsmal: sporsmalet, tekst: '', svar: null, bekreftet: false }]);
    try {
      const svar = await sporOss({ sporsmal: sporsmalet, sporsmalId: sporsmal?.id ?? null, instans: sporsmal?.instans ?? '' }, (bit) =>
        oppdaterSiste((innlegg) => ({ ...innlegg, tekst: innlegg.tekst + bit }))
      );
      oppdaterSiste((innlegg) => ({ ...innlegg, tekst: svar.svar, svar }));
    } catch (feilen) {
      setSamtale((s) => s.slice(0, -1));
      setTekst(sporsmalet);
      if (!onFeil(feilen)) setFeil(feilen instanceof ApiFeil ? feilen.kode : 'ukjent');
    } finally {
      setSvarer(false);
    }
  }

  async function bekreft(indeks: number, endring: Endring) {
    if (await onBekreftEndring(endring)) setSamtale((s) => s.map((innlegg, i) => (i === indeks ? { ...innlegg, bekreftet: true } : innlegg)));
  }

  return (
    <section aria-labelledby="spor-oss-tittel" className="kort overflow-hidden">
      <h2 id="spor-oss-tittel" className="text-[15px] font-semibold">
        <button
          type="button"
          aria-expanded={apen}
          aria-controls="spor-oss-innhold"
          onClick={() => setApen(!apen)}
          className="flex min-h-[3.5rem] w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50"
        >
          <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-aksent-50 text-aksent-700">
            <MessagesSquare className="h-4 w-4" />
          </span>
          <span className="flex-1">{t('reise.sporOss.tittel')}</span>
          <ChevronDown aria-hidden="true" className={'h-4 w-4 text-slate-400 transition-transform ' + (apen ? 'rotate-180' : '')} />
        </button>
      </h2>

      {apen && (
        <div id="spor-oss-innhold" className="border-t border-slate-100 px-5 pb-5 pt-4">
          <p className="mb-4 text-sm text-slate-600">{t('reise.sporOss.ingress')}</p>

          {samtale.length > 0 && (
            <ol aria-live="polite" className="mb-4 flex flex-col gap-4">
              {samtale.map((innlegg, i) => (
                <li key={i} className="flex flex-col gap-2">
                  <p className="ml-6 self-end rounded-xl rounded-br-sm bg-slate-900 px-3 py-2 text-sm text-white">
                    <span className="sr-only">{t('reise.sporOss.du')}: </span>
                    {innlegg.sporsmal}
                  </p>
                  <div className="mr-2 rounded-xl rounded-bl-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                    <span className="sr-only">{t('reise.sporOss.oss')}: </span>
                    {innlegg.tekst ? (
                      <p className="whitespace-pre-line">{innlegg.tekst}</p>
                    ) : (
                      <p role="status" className="text-slate-500">
                        {t('reise.sporOss.svarer')}
                      </p>
                    )}
                    {innlegg.svar && innlegg.svar.kilder.length > 0 && (
                      <p className="mt-2 text-xs text-slate-500">{t('reise.sporOss.kilder', { kilder: innlegg.svar.kilder.map((k) => k.tittel).join(', ') })}</p>
                    )}
                    {innlegg.svar?.detSjekkerVi && <p className="mt-2 text-xs text-slate-500">{t('reise.sporOss.sjekkerVi')}</p>}
                    {innlegg.svar?.foreslattEndring && (
                      <div className="mt-3 rounded-lg border border-aksent-200 bg-white p-3">
                        <p className="mb-3">
                          {t('reise.sporOss.endring', {
                            sporsmal: innlegg.svar.foreslattEndring.sporsmalTekst,
                            svar: endringstekst(innlegg.svar.foreslattEndring),
                          })}
                        </p>
                        {innlegg.bekreftet ? (
                          <p role="status" className="flex items-center gap-2 font-medium text-aksent-800">
                            <Check aria-hidden="true" className="h-4 w-4" />
                            {t('reise.sporOss.endret')}
                          </p>
                        ) : (
                          <button type="button" onClick={() => bekreft(i, innlegg.svar!.foreslattEndring!)} className="knapp-sekundaer w-full">
                            {t('reise.sporOss.bekreftEndring')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}

          {feil && (
            <p role="alert" className="mb-3 melding-feil text-sm">
              {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
            </p>
          )}

          <form onSubmit={send} className="flex flex-col gap-2">
            <label htmlFor="spor-oss-tekst" className="sr-only">
              {t('reise.sporOss.etikett')}
            </label>
            <textarea
              id="spor-oss-tekst"
              value={tekst}
              maxLength={2000}
              rows={3}
              placeholder={t('reise.sporOss.plassholder')}
              onChange={(e) => setTekst(e.target.value)}
              className="felt w-full resize-none text-sm"
            />
            <button type="submit" disabled={svarer || !tekst.trim()} className="knapp-primaer w-full">
              {svarer ? t('reise.sporOss.svarer') : t('reise.sporOss.send')}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
